import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Track if a 401-driven logout toast was already shown to avoid spamming the UI
// when multiple parallel requests fail at once.
let _sessionExpiredToastShown = false;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('capex_token'));
  const [loading, setLoading] = useState(true);

  const api = useCallback(() => {
    const instance = axios.create({
      baseURL: API,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    // Global 401 interceptor: gracefully handle expired/invalid tokens by
    // showing a clear French message and redirecting to /login (preserving
    // the current path so the user lands back after re-auth).
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const rawDetail = error?.response?.data?.detail;
        // Don't trigger on /auth/me probe at boot (handled separately) or login attempts
        const url = error?.config?.url || '';
        const isAuthProbe = url.includes('/auth/me') || url.includes('/auth/login');

        if (status === 401 && token && !isAuthProbe) {
          if (!_sessionExpiredToastShown) {
            _sessionExpiredToastShown = true;
            const friendly =
              typeof rawDetail === 'string' && rawDetail.toLowerCase().includes('expir')
                ? rawDetail
                : "Votre session a expiré ou est invalide. Veuillez vous reconnecter pour continuer.";
            toast.error(friendly, { duration: 6000, id: 'session-expired' });

            // Save the page we were on so we can resume after re-login
            try {
              const here = window.location.pathname + window.location.search;
              if (here && here !== '/login' && here !== '/') {
                sessionStorage.setItem('capex_post_login_redirect', here);
              }
            } catch (_) { /* ignore storage errors */ }

            // Clear auth state and bounce to /login after a short delay
            // so the user can read the toast before the page changes.
            setTimeout(() => {
              const admin = localStorage.getItem('capex_admin_token');
              if (admin) {
                // Fin de session « Voir comme » : retour au compte super admin
                localStorage.removeItem('capex_admin_token');
                localStorage.removeItem('capex_imp_name');
                localStorage.setItem('capex_token', admin);
                window.location.assign('/dashboard');
                return;
              }
              localStorage.removeItem('capex_token');
              window.location.assign('/login');
            }, 1500);
          }
        }
        return Promise.reject(error);
      }
    );
    return instance;
  }, [token]);

  useEffect(() => {
    const validateToken = async () => {
      if (token) {
        try {
          const response = await api().get('/auth/me');
          setUser(response.data);
        } catch (error) {
          console.error('Token validation failed:', error);
          localStorage.removeItem('capex_token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    validateToken();
  }, [token, api]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.removeItem('capex_admin_token');
    localStorage.removeItem('capex_imp_name');
    localStorage.setItem('capex_token', access_token);
    setToken(access_token);
    setUser(userData);
    // Reset session-expired guard so future expirations show the toast again
    _sessionExpiredToastShown = false;
    return userData;
  };

  const register = async (email, password, name, role = 'commercial') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    
    // Check if registration requires approval (new security flow)
    if (response.data.status === 'pending_approval') {
      // Don't set token or user - registration is pending approval
      return response.data;
    }
    
    // Legacy flow (if ever used) - immediate access
    const { access_token, user: userData } = response.data;
    if (access_token) {
      localStorage.setItem('capex_token', access_token);
      setToken(access_token);
      setUser(userData);
    }
    return userData || response.data;
  };

  const logout = () => {
    localStorage.removeItem('capex_token');
    localStorage.removeItem('capex_admin_token');
    localStorage.removeItem('capex_imp_name');
    setToken(null);
    setUser(null);
  };

  // « Voir comme » (super admin) : on garde le jeton admin pour revenir ensuite
  const impersonate = async (userId) => {
    const { data } = await api().post(`/admin/impersonate/${userId}`);
    localStorage.setItem('capex_admin_token', localStorage.getItem('capex_token'));
    localStorage.setItem('capex_imp_name', data.user.name || data.user.email);
    localStorage.setItem('capex_token', data.access_token);
    window.location.assign('/dashboard');
  };
  const stopImpersonation = () => {
    const admin = localStorage.getItem('capex_admin_token');
    localStorage.removeItem('capex_admin_token');
    localStorage.removeItem('capex_imp_name');
    if (admin) localStorage.setItem('capex_token', admin); else localStorage.removeItem('capex_token');
    window.location.assign('/dashboard');
  };
  const impersonatedName = localStorage.getItem('capex_admin_token') ? localStorage.getItem('capex_imp_name') : null;

  const value = {
    user,
    token,
    loading,
    login,
    impersonate,
    stopImpersonation,
    impersonatedName,
    register,
    logout,
    api,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isSuperAdmin: user?.role === 'super_admin',
    isCommercial: user?.role === 'commercial',
    isReadonly: user?.role === 'readonly',
    isPrestataire: user?.role === 'prestataire',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
